import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './apiConfig';

/**
 * Phase 5a — Reports board SHELL API (report/static/report/mvc collections).
 *
 * Read-only: the shell makes ZERO mutating HTTP calls. Report GENERATION
 * endpoints are 5b+ and are NOT defined here.
 *
 * Legacy sources:
 *  - collection/reports.js        → GET /api/v1.0/reports/
 *  - collection/reports_groups.js → GET /api/v1.0/master/v1.0.0/ahu/reports/groups/
 *  - collection/projects.js       → GET /api/v1.0/projects/ (+ ?page=N&name=…,
 *    pinned variant '?pinnned=true' — the TRIPLE-N param is the real backend
 *    param, copied verbatim like dashboardApi does)
 *  - collection/ahu.js            → GET /api/v1.0/ahu/report_project_units/<id>
 *    ?include_versions=true (fallback /api/v1.0/ahu/units/?include_versions=true
 *    when no project id — kept for parity with collection/ahu.js:14-19)
 */

/** Row of GET /api/v1.0/reports/ (MasterAHUReportSerializer). */
export interface IReportType {
  id: number;
  name: string;
  value: string;
  /** 'ahu' | 'project' | 'all_projects' | 'usage' (free-form in DB). */
  type: string;
  export_type?: string | null;
  /** Group NAME (serializer: source='group.name'). */
  group: string;
}

/** Row of GET /api/v1.0/master/v1.0.0/ahu/reports/groups/. */
export interface IReportGroup {
  id: number;
  name: string;
  value: string;
}

/** Row of GET /api/v1.0/projects/ (only the fields the report board reads). */
export interface IReportProject {
  id: number;
  name: string;
  quote_number?: string | null;
  [key: string]: unknown;
}

/** Per-AHU report enablement flags (AHUProjectUnitReportSerializer.get_reports). */
export interface IAhuReportFlags {
  /* The four `has_selected_*` flags are what the AHU list DISABLES a row on,
   * one per component selection report (`sectionFlagKey`). Each asks whether
   * the AHU holds a component with a model actually picked — `master_fan`,
   * `master_coil`, `master_gas_heater`, `master_media` — because those reports
   * are printed off that selected master row, so an AHU with the section and
   * no selection renders blank cells. *Generatable* flags, like
   * `fmi_assembly` and `ahu_acoustics`; they replaced the `has_*_section`
   * flags, which asked only whether the section existed.
   *
   * The `<report>_data` flags next to them are a different question: they
   * gate the LEFT MENU's buttons (`ReportsMenu`), not the list. */
  fan_data?: boolean;
  has_selected_fan?: boolean;
  coil_data?: boolean;
  has_selected_coil?: boolean;
  gas_heater_data?: boolean;
  has_selected_gas_heater?: boolean;
  evap_pack_data?: boolean;
  has_selected_evap_pack?: boolean;
  er_wheel_selection?: boolean;
  phx_selection?: boolean;
  uvc_selection?: boolean;
  /** dev-sync G / CB-2 (ahu_serializers.py:203) — ≥1 fan that is plenum
   *  arr3/arr4 or DWDI arr3 AND has a motor. Unlike the other four section
   *  reports this is the *generatable* flag, not a `has_*_section` flag. */
  fmi_assembly?: boolean;
  /** dev-sync G / CB-2 (ahu_serializers.py:204) — the ONLY non-boolean key in
   *  this bag; it widens the index signature below. */
  fmi_assembly_page_count?: number;
  /** ≥1 fan that is both selected (`master_fan`) and rated (`rating_datetime`)
   *  — i.e. the AHU has octave-band sound power to report. Like `fmi_assembly`
   *  this is a *generatable* flag, not a `has_*_section` one: an AHU can have
   *  fan sections and still be ineligible for AHU Acoustics. */
  ahu_acoustics?: boolean;
  [key: string]: boolean | number | undefined;
}

/** Row of GET /api/v1.0/ahu/report_project_units/<id>?include_versions=true —
 *  a FLAT list (mains + versions mixed); the UI regroups client-side
 *  (main_header.js:_groupAhusByMain :35-58). */
export interface IReportAhuRow {
  id: number;
  project_id: number;
  tag: string;
  reports: IAhuReportFlags;
  model?: string | null;
  qty?: number | null;
  sa_volume_max?: number | null;
  parent_version_id?: number | null;
  version_order?: number | null;
  static_version?: boolean;
  static_version_name?: string | null;
}

export const reportsBoardApi = createApi({
  reducerPath: 'reportsBoardApi',
  baseQuery,
  endpoints: (builder) => ({
    /**
     * Role-filtered report types. weight_calc is re-typed to 'ahu' here — the
     * legacy menu mutates the collection before ANY consumer reads it
     * (menu.js:54), so the transform is the faithful single point of truth.
     */
    getReportTypes: builder.query<IReportType[], void>({
      query: () => '/api/v1.0/reports/',
      transformResponse: (rows: IReportType[]): IReportType[] =>
        (rows ?? []).map((r) =>
          r.value === 'weight_calc' ? { ...r, type: 'ahu' } : r
        ),
    }),

    getReportGroups: builder.query<IReportGroup[], void>({
      query: () => '/api/v1.0/master/v1.0.0/ahu/reports/groups/',
    }),

    /**
     * Main projects list, 100/page. URL parity with collection/projects.js:
     * the boot fetch is the BARE list url (no params); every subsequent page /
     * search goes through filter() → '?page=N&name=…' (name may be empty when
     * scrolling unfiltered — legacy sends '&name=' too).
     */
    getReportProjects: builder.query<
      IReportProject[],
      { page?: number; name?: string } | void
    >({
      query: (args) => {
        const { page = 1, name = '' } = args ?? {};
        if (page === 1 && !name) return '/api/v1.0/projects/';
        return `/api/v1.0/projects/?page=${page}&name=${encodeURIComponent(name)}`;
      },
    }),

    /** Pinned projects — TRIPLE-N 'pinnned=true' (collection/projects.js:9). */
    getReportPinnedProjects: builder.query<IReportProject[], { name?: string } | void>({
      query: (args) => {
        const { name = '' } = args ?? {};
        if (!name) return '/api/v1.0/projects/?pinnned=true';
        return `/api/v1.0/projects/?pinnned=true&name=${encodeURIComponent(name)}`;
      },
    }),

    /**
     * Flat AHU list (mains + versions) with per-AHU report flags for the
     * selected project. Empty-string project id falls back to the all-units
     * url exactly like collection/ahu.js:10-20.
     */
    getReportProjectUnits: builder.query<IReportAhuRow[], string>({
      query: (projectId) =>
        projectId
          ? `/api/v1.0/ahu/report_project_units/${projectId}?include_versions=true`
          : '/api/v1.0/ahu/units/?include_versions=true',
    }),
  }),
});

export const {
  useGetReportTypesQuery,
  useGetReportGroupsQuery,
  useGetReportProjectsQuery,
  useLazyGetReportProjectsQuery,
  useGetReportPinnedProjectsQuery,
  useLazyGetReportPinnedProjectsQuery,
  useGetReportProjectUnitsQuery,
} = reportsBoardApi;
